import type { ReactNode } from 'react';
import { Logo } from '@/components/Logo';
import { TYPE } from '@/lib/typography';

interface AuthShellProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  brandLogoUrl?: string | null;
  brandLogoAlt?: string;
}

/**
 * Shared centered card used by sign-in / sign-up / forgot-password / reset.
 * Pre-login: follows stored/system colour preference via `html.dark`.
 */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
  brandLogoUrl,
  brandLogoAlt,
}: AuthShellProps) {
  return (
    <div className="flex min-h-screen flex-col justify-center bg-ds-bg py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          {brandLogoUrl ? (
            <span className="logo-plate">
              <img
                src={brandLogoUrl}
                alt={brandLogoAlt ?? ''}
                className="h-16 w-auto max-w-[220px] object-contain"
              />
            </span>
          ) : (
            <Logo size={64} />
          )}
        </div>
        <h2 className={`mt-6 text-center text-ds-text ${TYPE.display}`}>{title}</h2>
        {subtitle && <p className={`mt-2 text-center text-ds-muted ${TYPE.body}`}>{subtitle}</p>}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-ds-card px-4 py-8 shadow-card sm:rounded-lg sm:px-10">{children}</div>
        {footer && <div className={`mt-4 text-center text-ds-muted ${TYPE.body}`}>{footer}</div>}
      </div>
    </div>
  );
}
